import { Formik, FormikHelpers, FormikProps } from 'formik';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { UiInfoCircle } from '@vyaguta/vyaguta-icons/ui';

import LFModal from 'components/common/LFModal';
import DropDown from 'components/common/dropDown';
import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';

import { mixPanelEvents } from 'constants/mixPanel';
import { CHANGE_APPRAISER_NOTE, REQUEST_FOR_CHANGE_OF_APPRAISERS } from 'constants/dashboard';

import { changeAppraiserRequestSchema } from 'schemas/changeAppraiserRequest';
import { ChangeAppraiserRequest } from 'interface/appraiserRequest';

import * as mixpanel from 'services/mixPanel';

import { useCreateAppraiserChangeRequestMutation } from 'hooks/query/useTeamQueries';

import { success } from 'utils/toast';

interface AllocationOption {
  label: string;
  value: number | string;
}

interface AllocationItem {
  name: string;
  id: number | string;
}

interface Props {
  isOpen?: boolean;
  filteredAllocation: AllocationItem[];
  onClose: () => void;
  TriggerBtn?: React.ComponentType<{ onClick: () => void; interactive: boolean; isLoading?: boolean }>;
  titleText?: string;
}

const ChangeAppraiserModal: React.FC<Props> = (props) => {
  const { isOpen, filteredAllocation, onClose, TriggerBtn, titleText } = props;

  const { mutateAsync: createAppraiserChangeRequest, isPending: isSubmitting } =
    useCreateAppraiserChangeRequestMutation();

  const filteredOption: AllocationOption[] = useMemo(
    () =>
      filteredAllocation.map((allocation) => ({
        label: allocation.name,
        value: allocation.id,
      })),
    [filteredAllocation]
  );

  const initialValues: ChangeAppraiserRequest = {
    reason: '',
    allocationId: filteredOption.length === 1 ? Number(filteredOption[0].value) : 0,
  };

  const handleFormSubmit = async (
    body: ChangeAppraiserRequest,
    _submitProps: FormikHelpers<ChangeAppraiserRequest>
  ) => {
    const { allocationId, reason } = body;

    const data = (await createAppraiserChangeRequest({ allocationId, reason })) as { message: string };

    onClose();
    success({ title: 'Success', message: data.message });

    mixpanel.trackEvent(mixPanelEvents.select.appraiserChangeRequest.trigger.requestForChange);
  };

  return (
    <LFModal
      TriggerBtn={TriggerBtn}
      isOpen={isOpen}
      onClose={onClose}
      btnLabel="Change Appraiser"
      closeButton={true}
      title={{ text: titleText || REQUEST_FOR_CHANGE_OF_APPRAISERS, type: 'info' }}
      className="change-appraiser-request"
      htmlContent={
        <Formik<ChangeAppraiserRequest>
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
          validationSchema={changeAppraiserRequestSchema}
        >
          {({
            handleSubmit,
            errors,
            values,
            dirty,
            setFieldValue,
            touched,
            setFieldTouched,
          }: FormikProps<ChangeAppraiserRequest>) => {
            return (
              <form onSubmit={handleSubmit} className="change-appraiser-request__form">
                <div className="change-appraiser-request__form-body">
                  <InputWrapper label="Allocation" error={touched.allocationId && errors.allocationId} isMandatory>
                    <DropDown
                      placeholder="Select an allocation"
                      hasError={!!(touched.allocationId && errors.allocationId)}
                      isDisabled={isSubmitting}
                      name="allocationId"
                      onDropDownChange={(option) => {
                        const { value } = option as AllocationOption;
                        setFieldTouched('allocationId', true, true);
                        setFieldValue('allocationId', value);
                      }}
                      options={filteredOption}
                      value={filteredOption.find(({ value }) => value === values.allocationId) ?? null}
                    />
                  </InputWrapper>

                  <InputWrapper isMandatory label="Reason" error={touched.reason && errors.reason}>
                    <textarea
                      name="reason"
                      placeholder="Type your reason..."
                      className={classNames(
                        'form-elem font-weight-regular custom-scroll-bar change-appraiser-request__reason',
                        {
                          'form-elem--error': touched.reason && errors.reason,
                        }
                      )}
                      value={values.reason}
                      onChange={(e) => {
                        setFieldTouched('reason', true, true);
                        setFieldValue('reason', e.target.value);
                      }}
                      disabled={isSubmitting}
                    />
                  </InputWrapper>

                  <div className="change-appraiser-request__note">
                    <UiInfoCircle size="14" />
                    <div>{CHANGE_APPRAISER_NOTE}</div>
                  </div>
                </div>

                <div className="change-appraiser-request__footer">
                  <button
                    type="button"
                    onClick={() => handleSubmit()}
                    className={classNames('btn card-button change-appraiser-request__btn', {
                      'btn--primary': !isSubmitting && dirty,
                      'btn--disabled': isSubmitting || !dirty,
                    })}
                    disabled={isSubmitting || !dirty}
                  >
                    <span className={classNames({ invisible: isSubmitting })}>Request For Change </span>
                    {isSubmitting && <Loading />}
                  </button>

                  <button
                    type="button"
                    className={classNames('btn card-button ml-10 change-appraiser-request__btn', {
                      'btn--outlined-grey': !isSubmitting,
                      'btn--disabled': isSubmitting,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();

                      mixpanel.trackEvent(mixPanelEvents.select.appraiserChangeRequest.trigger.cancel);
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            );
          }}
        </Formik>
      }
    />
  );
};

export default ChangeAppraiserModal;
